import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ThemeToggle } from "@/components/theme-toggle";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authClient } from "@/lib/auth-client";
import { getSafePostAuthRedirectTarget } from "@/lib/two-factor-policy";

type VerificationMethod = "totp" | "backup";

const LOGO_URL =
	"https://cdn.prod.website-files.com/64493d11d62bbdf4ac3b43ef/644965713d613f2767b8f9fa_5da4ec2b220488c9b249d5dc_Anthem%20Logo_sq-01.webp";

export default function Verify2FAPage() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const redirectTarget = React.useMemo(
		() => getSafePostAuthRedirectTarget(searchParams.get("redirect")),
		[searchParams],
	);

	const [code, setCode] = React.useState("");
	const [loading, setLoading] = React.useState(false);
	const [error, setError] = React.useState<string | null>(null);
	const [method, setMethod] = React.useState<VerificationMethod>("totp");

	const handleVerify = React.useCallback(
		async (event: React.FormEvent<HTMLFormElement>) => {
			event.preventDefault();
			setError(null);

			if (!code || code.length < 6) {
				setError("Please enter a valid code");
				return;
			}

			setLoading(true);

			try {
				if (method === "totp") {
					const result = await authClient.twoFactor.verifyTotp({
						code,
						trustDevice: true,
					});
					if (result.error) {
						setError(result.error.message || "Invalid verification code");
						setLoading(false);
						return;
					}
				} else {
					const result = await authClient.twoFactor.verifyBackupCode({
						code,
						trustDevice: true,
					});
					if (result.error) {
						setError(result.error.message || "Invalid backup code");
						setLoading(false);
						return;
					}
				}

				const maxWaitMs = 3000;
				const pollIntervalMs = 100;
				const startTime = Date.now();

				while (Date.now() - startTime < maxWaitMs) {
					const session = await authClient.getSession();
					if (session.data?.user) {
						navigate(redirectTarget);
						return;
					}
					await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
				}

				setError(
					"Verified, but we couldn't load your session. Try again, or sign in once more.",
				);
				setLoading(false);
			} catch {
				setError("An error occurred during verification");
				setLoading(false);
			}
		},
		[code, method, navigate, redirectTarget],
	);

	const switchMethod = React.useCallback(() => {
		setMethod((prev) => (prev === "totp" ? "backup" : "totp"));
		setCode("");
		setError(null);
	}, []);

	return (
		<div className="flex min-h-screen items-center justify-center p-4">
			<div className="absolute top-4 right-4">
				<ThemeToggle />
			</div>
			<Card className="w-full max-w-md">
				<CardHeader className="space-y-1">
					<div className="mb-4 flex justify-center">
						<img src={LOGO_URL} alt="Cherry" className="h-24" />
					</div>
					<CardTitle className="text-center text-2xl">
						Cherry sign-in verification
					</CardTitle>
					<CardDescription className="text-center">
						{method === "totp"
							? "Enter the 6-digit code from your authenticator app"
							: "Enter one of your backup codes"}
					</CardDescription>
				</CardHeader>
				<CardContent>
					<form onSubmit={handleVerify} className="space-y-4">
						{error && (
							<div className="rounded-md bg-destructive/15 p-3 text-center text-sm text-destructive">
								{error}
							</div>
						)}

						<div className="space-y-2">
							<Label htmlFor="code">
								{method === "totp" ? "Verification Code" : "Backup Code"}
							</Label>
							<Input
								id="code"
								name="code"
								type="text"
								placeholder={method === "totp" ? "000000" : "XXXXXXXXXX"}
								value={code}
								onChange={(event) => setCode(event.target.value)}
								required
								disabled={loading}
								autoComplete="one-time-code"
								className="text-center text-lg tracking-widest"
								maxLength={method === "totp" ? 6 : 10}
							/>
						</div>

						<Button type="submit" disabled={loading} className="w-full">
							{loading ? "Verifying..." : "Verify"}
						</Button>

						<div className="text-center">
							<Button
								type="button"
								variant="link"
								onClick={switchMethod}
								disabled={loading}
								className="text-sm"
							>
								{method === "totp"
									? "Use a backup code instead"
									: "Use authenticator app instead"}
							</Button>
						</div>

						<div className="text-center">
							<Button
								type="button"
								variant="ghost"
								onClick={() =>
									navigate(
										`/login?redirect=${encodeURIComponent(redirectTarget)}`,
									)
								}
								disabled={loading}
								className="text-sm text-muted-foreground"
							>
								Back to login
							</Button>
						</div>
					</form>
				</CardContent>
			</Card>
		</div>
	);
}
